import React, { FC } from 'react';
import { DataGrid, ptBR } from '@mui/x-data-grid';
import { Box, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { format } from 'date-fns';
import { formatNumberToBRL } from 'utils/formatters';
import { IWithdrawalRequest } from 'common/withdrawal-request.interface';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import PaymentStatusPill from '../../Banking/Dashboard/pages/PaymentsPage/components/PaymentStatusPill';

export interface ManualWithdrawTableProps {
  rows: IWithdrawalRequest[];
  rowCount: number;
  loading: boolean;
  paginationModel: { page: number; pageSize: number };
  onPaginationModelChange: (model: { page: number; pageSize: number }) => void;
  onOpenView: (row: IWithdrawalRequest, source: 'status' | 'eye') => void;
}

const WithdrawalStatusPill: FC<{
  status: string;
  row: IWithdrawalRequest;
  onOpenView: (row: IWithdrawalRequest, source: 'status' | 'eye') => void;
}> = ({ status, row, onOpenView }) => {
  const value = String(status || '').toLowerCase();

  const styles = {
    pending: { bg: '#FFE2B8', color: '#9A3412', icon: <TouchAppIcon sx={{ fontSize: 15, marginRight: '3px' }} /> },
    approved: { bg: '#EAF7EA', color: '#1F7A1F', icon: <CheckIcon sx={{ fontSize: 15, marginRight: '3px' }} /> },
    rejected: { bg: '#FDEAEA', color: '#B91C1C', icon: <ReportGmailerrorredIcon sx={{ fontSize: 15, marginRight: '3px' }} /> },
    expired: { bg: '#EDF3F8', color: '#5F7D95', icon: <AccessTimeIcon sx={{ fontSize: 15, marginRight: '3px' }} /> },
  } as const;

  const current = styles[value as keyof typeof styles] || styles.pending;

  let label: string;

  switch (value) {
    case 'approved':
      label = 'Aprovado';
      break;
    case 'rejected':
      label = 'Rejeitado';
      break;
    case 'expired':
      label = 'Expirado';
      break;
    default:
      label = 'Revisão';
  }

  const isVisualizar = label === 'Revisão';

  const content = (
    <Box
      sx={{
        px: 1.5,
        py: 0.4,
        borderRadius: '999px',
        backgroundColor: current.bg,
        color: current.color,
        fontSize: 12,
        fontWeight: 500,
        width: 'fit-content',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isVisualizar ? 'pointer' : 'default',
        ...(isVisualizar && {
          '&:hover': {
            boxShadow: 1,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: 0,
          },
        }),
      }}
      onClick={isVisualizar ? () => onOpenView(row, 'status') : undefined}
    >
      {current.icon}
      {label}
    </Box>
  );

  return content;
};

const AccountDisplay = ({ legalName, tradeName, bankInfo }: { legalName?: string; tradeName?: string; bankInfo?: string }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      py: 1,
      width: '100%',
      overflow: 'hidden',
    }}
  >
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      title={legalName}
    >
      {legalName || '-'}
    </Typography>

    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      title={tradeName}
    >
      {tradeName || '-'}
    </Typography>

    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        lineHeight: 1.2,
        mt: 0.25,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {bankInfo}
    </Typography>
  </Box>
);

const ManualWithdrawTable: React.FC<ManualWithdrawTableProps> = ({
  rows,
  rowCount,
  loading,
  paginationModel,
  onPaginationModelChange,
  onOpenView,
}) => {
  return (
    <DataGrid
      rowHeight={72}
      sx={{
        border: 'none',
        width: '100%',
        '& .MuiDataGrid-row': { border: 'none', backgroundColor: 'transparent !important', borderBottom: '1px solid #E0E0E0' },
        '& .MuiDataGrid-row:last-child': { borderBottom: 'none' },
        '& .MuiDataGrid-row:hover': { backgroundColor: 'transparent !important' },
        '& .MuiDataGrid-row.Mui-selected': { backgroundColor: 'transparent !important' },
        '& .MuiDataGrid-cell': { borderBottom: 'none', fontWeight: 400 },
        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F7F6F6', borderBottom: 'none' },
        '& .MuiDataGrid-columnSeparator': { display: 'none' },
      }}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={[5, 10, 25, 50]}
      columns={[
        {
          field: 'id',
          headerName: '',
          flex: 1,
          align: 'left',
          sortable: false,
          filterable: false,
        },
        {
          field: 'createdAt',
          headerName: 'Data',
          flex: 1,
          align: 'left',
          sortable: false,
          filterable: false,
          renderCell: (params) => format(new Date(params.value), 'dd/MM/yyyy'),
        },
        {
          field: 'status',
          headerName: 'Status Solicitação',
          headerAlign: 'center',
          flex: 1,
          align: 'center',
          sortable: false,
          filterable: false,
          renderCell: (params) => <WithdrawalStatusPill status={params.value} row={params.row} onOpenView={onOpenView} />,
        },
        {
          field: 'transactionStatus',
          headerName: 'Status Transação',
          headerAlign: 'center',
          flex: 1,
          align: 'center',
          sortable: false,
          filterable: false,
          renderCell: (params) => {
            const latestErrorEvent = [...(params.row.transaction?.events || [])]
              .reverse()
              .find((event) => event.status === 'ERROR' && event.errorMessage);

            const content = params.row.transaction?.status ? <PaymentStatusPill status={params.row.transaction.status} /> : '-';

            if (!latestErrorEvent?.errorMessage) {
              return content;
            }

            return (
              <Tooltip arrow placement="top" title={latestErrorEvent.errorMessage}>
                <Box sx={{ display: 'inline-flex' }}>{content}</Box>
              </Tooltip>
            );
          },
        },
        {
          field: 'originAccount',
          headerName: 'Origem',
          headerAlign: 'center',
          flex: 1,
          align: 'center',
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <AccountDisplay
              legalName={params.value?.businessName}
              tradeName={params.value?.tradeName}
              bankInfo={`${params.value?.bankCode || '-'} | ${params.value?.bankBranch || '-'} | ${params.value?.bankAccount || '-'}`}
            />
          ),
        },
        {
          field: 'counterparty',
          headerName: 'Destinatário',
          headerAlign: 'center',
          flex: 1,
          align: 'center',
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {params.row.type === 'COUNTERPARTY' ? (
                <AccountDisplay
                  legalName={params.value?.businessName}
                  tradeName={params.value?.tradeName}
                  bankInfo={`${params.value?.bankCode || '-'} | ${params.value?.bankBranch || '-'} | ${params.value?.bankAccount || '-'}`}
                />
              ) : (
                <AccountDisplay
                  legalName={params.row.paymentAccount?.businessName}
                  tradeName={params.row.paymentAccount?.tradeName}
                  bankInfo={`${params.row.paymentAccount?.bankCode || '-'} | ${params.row.paymentAccount?.bankBranch || '-'} | ${
                    params.row.paymentAccount?.bankAccount || '-'
                  }`}
                />
              )}
            </>
          ),
        },
        {
          field: 'value',
          headerName: 'Valor Transferência',
          flex: 1,
          align: 'left',
          sortable: false,
          filterable: false,
          renderCell: (params) => formatNumberToBRL(params.value),
        },
        {
          field: 'commentary',
          headerName: 'Detalhe',
          flex: 1,
          align: 'center',
          headerAlign: 'center',
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              <VisibilityOutlinedIcon
                sx={{ fontSize: 24, color: '#000000', cursor: 'pointer' }}
                onClick={() => onOpenView(params.row, 'eye')}
              />
            </Box>
          ),
        },
      ]}
      rows={rows}
      rowCount={rowCount}
      columnVisibilityModel={{ id: false }}
      sortModel={[{ field: 'id', sort: 'desc' }]}
      getRowId={(row) => row.id}
      localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      autoHeight
      loading={loading}
    />
  );
};

export default ManualWithdrawTable;
